import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../lib/api';

interface PricingTier {
  name: string;
  display_name: string;
  description: string | null;
  scrapes_limit: number;
  price_monthly: number;
  features: string[];
  sort_order: number;
}

const DEFAULT_TIERS: PricingTier[] = [
  {
    name: 'free',
    display_name: 'Free',
    description: 'Get started with basic transforms',
    scrapes_limit: 10,
    price_monthly: 0,
    features: ['10 transforms/month', 'Single page transform', 'YouTube transcription'],
    sort_order: 0,
  },
  {
    name: 'pro',
    display_name: 'Pro',
    description: 'For power users and researchers',
    scrapes_limit: 100,
    price_monthly: 9.99,
    features: ['100 transforms/month', 'Multi-page crawling', 'Priority support', 'Note refinement'],
    sort_order: 1,
  },
  {
    name: 'unlimited',
    display_name: 'Unlimited',
    description: 'No limits for teams and enterprises',
    scrapes_limit: -1,
    price_monthly: 29.99,
    features: ['Unlimited transforms', 'Multi-page crawling', 'Priority support', 'Note refinement', 'API access'],
    sort_order: 2,
  },
];

export function PricingPage() {
  const [tiers, setTiers] = useState<PricingTier[]>(DEFAULT_TIERS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest<{ tiers: PricingTier[] }>('/api/pricing')
      .then(data => setTiers(data.tiers))
      .catch(() => setTiers(DEFAULT_TIERS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-surface-950">
      <div className="max-w-5xl mx-auto p-6 lg:p-8">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-surface-400 hover:text-white transition-colors mb-8"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to app
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Choose Your Plan</h1>
          <p className="text-surface-400 text-lg max-w-2xl mx-auto">
            Start for free and upgrade as your needs grow. All plans include access to our core scraping and content transformation tools.
          </p>
        </div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`card p-6 flex flex-col ${
                  tier.name === 'pro'
                    ? 'ring-2 ring-primary-500 relative'
                    : ''
                }`}
              >
                {tier.name === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Popular
                  </div>
                )}

                <h2 className="text-2xl font-bold text-white mb-1">{tier.display_name}</h2>
                <p className="text-surface-400 text-sm mb-4">{tier.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    ${tier.price_monthly.toFixed(2)}
                  </span>
                  <span className="text-surface-400">/month</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {(Array.isArray(tier.features) ? tier.features : []).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-surface-300">
                      <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  className={
                    tier.name === 'pro' ? 'btn-primary w-full' : 'btn-secondary w-full'
                  }
                >
                  {tier.price_monthly === 0 ? 'Get Started' : 'Coming Soon'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
